import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Input } from '@bruno/ui';
import {
  changePassword,
  disableTwoFactor,
  getTwoFactorStatus,
  regenerateRecoveryCodes,
  setupTwoFactor,
  verifyTwoFactor,
} from '../api/account';
import { useAuth } from '../auth/AuthContext';

export function SettingsAccountPage() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold">Account</h1>
        <p className="text-sm text-slate-500">{user.email}</p>
      </div>
      <PasswordCard twoFactorEnabled={user.twoFactorEnabled} />
      <TwoFactorCard />
    </div>
  );
}

function PasswordCard({ twoFactorEnabled }: { twoFactorEnabled: boolean }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [done, setDone] = useState(false);

  const mut = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
      setCode('');
      setDone(true);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setDone(false);
    if (next.length < 8) return mut.reset();
    if (next !== confirm) return mut.reset();
    mut.mutate({
      currentPassword: current,
      newPassword: next,
      twoFactorCode: twoFactorEnabled && code.trim() ? code.trim() : undefined,
    });
  }

  const localError =
    next.length > 0 && next.length < 8
      ? 'Password must be at least 8 characters'
      : confirm.length > 0 && next !== confirm
        ? 'Passwords do not match'
        : null;

  return (
    <Card>
      <h2 className="text-lg font-semibold">Change password</h2>
      <p className="text-sm text-slate-500">
        After a successful change every other device is signed out.
      </p>
      <form onSubmit={submit} className="flex flex-col gap-4 mt-4">
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          minLength={8}
          required
        />
        <Input
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          minLength={8}
          required
        />
        {twoFactorEnabled ? (
          <Input
            label="Authenticator code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            required
          />
        ) : null}
        {localError ? <div className="text-sm text-red-600">{localError}</div> : null}
        {mut.isError && !localError ? (
          <div className="text-sm text-red-600">
            {(mut.error as Error)?.message ?? 'Password change failed'}
          </div>
        ) : null}
        {done ? (
          <div className="text-sm text-emerald-600">Password updated.</div>
        ) : null}
        <Button type="submit" loading={mut.isPending} disabled={!!localError}>
          Change password
        </Button>
      </form>
    </Card>
  );
}

interface SetupState {
  otpauthUri: string;
  qrDataUrl: string;
  secret: string;
}

function TwoFactorCard() {
  const qc = useQueryClient();
  const { refreshUser } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['2fa-status'],
    queryFn: getTwoFactorStatus,
  });

  const [setup, setSetup] = useState<SetupState | null>(null);
  const [enrollCode, setEnrollCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disableMode, setDisableMode] = useState<'totp' | 'recovery'>('totp');
  const [regenPassword, setRegenPassword] = useState('');
  const [regenCode, setRegenCode] = useState('');

  const setupMut = useMutation({
    mutationFn: setupTwoFactor,
    onSuccess: (res) => {
      setSetup(res);
      setEnrollCode('');
      setRecoveryCodes(null);
    },
  });
  const verifyMut = useMutation({
    mutationFn: verifyTwoFactor,
    onSuccess: (res) => {
      setRecoveryCodes(res.recoveryCodes);
      setSetup(null);
      setEnrollCode('');
      void qc.invalidateQueries({ queryKey: ['2fa-status'] });
      // Flip the auth-context user's `twoFactorEnabled` so the nudge
      // banner clears immediately, without a reload or manual dismiss.
      void refreshUser();
    },
  });
  const disableMut = useMutation({
    mutationFn: disableTwoFactor,
    onSuccess: () => {
      setDisablePassword('');
      setDisableCode('');
      setRecoveryCodes(null);
      void qc.invalidateQueries({ queryKey: ['2fa-status'] });
      void refreshUser();
    },
  });
  const regenMut = useMutation({
    mutationFn: regenerateRecoveryCodes,
    onSuccess: (res) => {
      setRecoveryCodes(res.recoveryCodes);
      setRegenPassword('');
      setRegenCode('');
      void qc.invalidateQueries({ queryKey: ['2fa-status'] });
    },
  });

  if (isLoading || !data) {
    return (
      <Card>
        <div className="text-sm text-slate-500">Loading two-factor settings…</div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Two-factor authentication</h2>
        <Badge tone={data.enabled ? 'green' : 'gray'}>{data.enabled ? 'On' : 'Off'}</Badge>
      </div>
      <p className="text-sm text-slate-500 mt-1">
        Add a one-time code from an authenticator app to your sign-in.
      </p>

      {recoveryCodes ? (
        <RecoveryCodesPanel codes={recoveryCodes} onDismiss={() => setRecoveryCodes(null)} />
      ) : null}

      {!data.enabled ? (
        <div className="mt-4 flex flex-col gap-3">
          {setup ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-slate-700">
                Scan this QR with your authenticator app, then enter the 6-digit code it
                shows.
              </p>
              <div className="flex items-center gap-4 flex-wrap">
                <img
                  src={setup.qrDataUrl}
                  alt="Two-factor QR code"
                  className="w-44 h-44 rounded-md border border-slate-200"
                />
                <div className="text-xs text-slate-500 break-all max-w-xs">
                  Can't scan? Enter this code manually:
                  <code className="block mt-1 p-2 bg-slate-100 rounded">{setup.secret}</code>
                </div>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  verifyMut.mutate({ code: enrollCode.trim() });
                }}
                className="flex flex-col gap-3 mt-2"
              >
                <Input
                  label="Code from your authenticator app"
                  value={enrollCode}
                  onChange={(e) => setEnrollCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
                {verifyMut.isError ? (
                  <div className="text-sm text-red-600">
                    {(verifyMut.error as Error)?.message ?? 'Verification failed'}
                  </div>
                ) : null}
                <div className="flex gap-2">
                  <Button type="submit" loading={verifyMut.isPending}>
                    Verify and enable
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setSetup(null);
                      setEnrollCode('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {setupMut.isError ? (
                <div className="text-sm text-red-600">
                  {(setupMut.error as Error)?.message ?? 'Could not start enrollment'}
                </div>
              ) : null}
              <Button onClick={() => setupMut.mutate()} loading={setupMut.isPending}>
                Set up two-factor authentication
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-6">
          <div className="text-sm text-slate-600">
            Enrolled{' '}
            {data.enrolledAt ? new Date(data.enrolledAt).toLocaleString() : '—'}.{' '}
            {data.recoveryCodesRemaining} of 10 recovery codes remaining.
          </div>

          <details className="border border-slate-200 rounded-md p-4">
            <summary className="font-medium cursor-pointer">Regenerate recovery codes</summary>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                regenMut.mutate({
                  currentPassword: regenPassword,
                  code: regenCode.trim(),
                });
              }}
              className="flex flex-col gap-3 mt-3"
            >
              <Input
                label="Current password"
                type="password"
                value={regenPassword}
                onChange={(e) => setRegenPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <Input
                label="Authenticator code"
                value={regenCode}
                onChange={(e) => setRegenCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />
              {regenMut.isError ? (
                <div className="text-sm text-red-600">
                  {(regenMut.error as Error)?.message ?? 'Regeneration failed'}
                </div>
              ) : null}
              <Button type="submit" variant="secondary" loading={regenMut.isPending}>
                Generate new codes
              </Button>
            </form>
          </details>

          <details className="border border-red-200 rounded-md p-4">
            <summary className="font-medium cursor-pointer text-red-700">
              Disable two-factor authentication
            </summary>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                disableMut.mutate({
                  currentPassword: disablePassword,
                  code: disableMode === 'totp' ? disableCode.trim() : undefined,
                  recoveryCode: disableMode === 'recovery' ? disableCode.trim() : undefined,
                });
              }}
              className="flex flex-col gap-3 mt-3"
            >
              <Input
                label="Current password"
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <Input
                label={disableMode === 'totp' ? 'Authenticator code' : 'Recovery code'}
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                inputMode={disableMode === 'totp' ? 'numeric' : 'text'}
                autoComplete="one-time-code"
                required
              />
              <button
                type="button"
                className="text-xs text-indigo-600 hover:underline self-start"
                onClick={() => {
                  setDisableMode((m) => (m === 'totp' ? 'recovery' : 'totp'));
                  setDisableCode('');
                }}
              >
                {disableMode === 'totp'
                  ? 'Use a recovery code instead'
                  : 'Use my authenticator app instead'}
              </button>
              {disableMut.isError ? (
                <div className="text-sm text-red-600">
                  {(disableMut.error as Error)?.message ?? 'Could not disable 2FA'}
                </div>
              ) : null}
              <Button type="submit" variant="danger" loading={disableMut.isPending}>
                Turn off two-factor authentication
              </Button>
            </form>
          </details>
        </div>
      )}
    </Card>
  );
}

function RecoveryCodesPanel({
  codes,
  onDismiss,
}: {
  codes: string[];
  onDismiss: () => void;
}) {
  const text = codes.join('\n');
  return (
    <div className="mt-4 border-2 border-amber-300 bg-amber-50 rounded-md p-4">
      <h3 className="font-semibold text-amber-900">Save these recovery codes</h3>
      <p className="text-sm text-amber-900 mt-1">
        Store them somewhere safe (a password manager or printed copy). They'll get you
        back in if you lose your authenticator device. Each code works only once and you
        won't be able to see them again.
      </p>
      <pre className="mt-3 text-sm bg-white border border-amber-200 rounded p-3 font-mono whitespace-pre">
        {text}
      </pre>
      <div className="flex gap-2 mt-3">
        <Button
          size="sm"
          variant="secondary"
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(text);
          }}
        >
          Copy all
        </Button>
        <Button
          size="sm"
          variant="secondary"
          type="button"
          onClick={() => {
            const blob = new Blob([`${text}\n`], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'bruno-recovery-codes.txt';
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          Download .txt
        </Button>
        <Button size="sm" variant="ghost" type="button" onClick={onDismiss}>
          I've saved them
        </Button>
      </div>
    </div>
  );
}
